'use client'

import { useEffect, useState, useCallback } from 'react'

const KEY = 'blc.pro.v1'
const TRIAL_DAYS = 30

type ProState = {
  /** Date d'expiration en ms epoch. 0 = pas Pro. */
  expiresAt: number
  /** Plan : 'trial' (essai gratuit) ou 'paid' (1.99€/mois). */
  plan?: 'trial' | 'paid'
}

function read(): ProState {
  if (typeof window === 'undefined') return { expiresAt: 0 }
  try {
    const raw = window.localStorage.getItem(KEY)
    return raw ? JSON.parse(raw) as ProState : { expiresAt: 0 }
  } catch { return { expiresAt: 0 } }
}

function write(s: ProState) {
  if (typeof window === 'undefined') return
  try { window.localStorage.setItem(KEY, JSON.stringify(s)) } catch {}
  window.dispatchEvent(new CustomEvent('blc-pro'))
}

export function usePro() {
  const [state, setState] = useState<ProState>({ expiresAt: 0 })

  useEffect(() => {
    setState(read())
    const onChange = () => setState(read())
    window.addEventListener('storage', onChange)
    window.addEventListener('blc-pro', onChange as any)
    return () => {
      window.removeEventListener('storage', onChange)
      window.removeEventListener('blc-pro', onChange as any)
    }
  }, [])

  const active = state.expiresAt > Date.now()

  const startTrial = useCallback(() => {
    const next = { expiresAt: Date.now() + TRIAL_DAYS * 86400_000, plan: 'trial' as const }
    write(next); setState(next)
  }, [])

  const subscribe = useCallback(() => {
    // Démo : on simule la souscription. Dans une vraie prod : Stripe Checkout.
    const next = { expiresAt: Date.now() + 365 * 86400_000, plan: 'paid' as const }
    write(next); setState(next)
  }, [])

  const cancel = useCallback(() => {
    write({ expiresAt: 0 }); setState({ expiresAt: 0 })
  }, [])

  return { active, plan: state.plan, expiresAt: state.expiresAt, startTrial, subscribe, cancel }
}

export function fmtRemaining(expiresAt: number): string {
  if (expiresAt <= 0) return ''
  const ms = expiresAt - Date.now()
  if (ms <= 0) return 'Expiré'
  const days = Math.ceil(ms / 86400_000)
  if (days <= 1) return 'Moins d\'un jour'
  return `${days} jours restants`
}
